import { useBreadcrumbs, useNode } from "@mapcraft-tasks/client-store/react";
import { useMemo, useState } from "react";
import { type ArchiveTreeNode, buildArchiveForest, useArchive } from "../lib/archive";
import { labelOf } from "./node-page";
import { Chevron, GUIDE_OFFSET, GUTTER_LEFT, INDENT } from "./outline/row";

/**
 * Архив деревом. Только чтение: архивная нода заморожена (spec-tasks, «Архивная нода —
 * read-only»), а в реплику она не входит — аутлайн-движок работать с ней не может.
 *
 * Каждая выкинутая ветка подписана тем, ОТКУДА её выкинули: живым путём до родителя.
 * Без этого список корней — снова свалка заголовков, только покороче.
 * Ветки свёрнуты: список корней и есть оглавление архива.
 */
export function ArchiveTree() {
	const rows = useArchive();
	const forest = useMemo(() => (rows ? buildArchiveForest(rows) : []), [rows]);
	const branchIds = useMemo(() => {
		const ids: string[] = [];
		const walk = (node: ArchiveTreeNode) => {
			if (node.children.length > 0) {
				ids.push(node.row.id);
				node.children.forEach(walk);
			}
		};
		forest.forEach(walk);
		return ids;
	}, [forest]);
	const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

	if (rows === null) {
		return <p className="text-sm font-light text-ink-muted">нет связи — архив недоступен оффлайн</p>;
	}
	if (forest.length === 0) {
		return <p className="text-sm font-light text-ink-muted">архив пуст</p>;
	}

	const toggle = (id: string) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			if (!next.delete(id)) {
				next.add(id);
			}
			return next;
		});
	const allOpen = branchIds.length > 0 && branchIds.every((id) => expanded.has(id));

	return (
		<div>
			{branchIds.length > 0 && (
				<button
					type="button"
					className="mb-2 text-xs font-light text-ink-muted transition-colors hover:text-ink-strong"
					onClick={() => setExpanded(allOpen ? new Set() : new Set(branchIds))}
				>
					{allOpen ? "свернуть всё" : "развернуть всё"}
				</button>
			)}
			{forest.map((root) => (
				<section key={root.row.id} className="mb-3">
					<Origin parentId={root.row.parentId} />
					<Branch node={root} depth={0} parentOp={null} expanded={expanded} onToggle={toggle} />
				</section>
			))}
		</div>
	);
}

/** Живой путь до места, откуда ветку выкинули. Туда же она и вернётся по restore. */
function Origin({ parentId }: { parentId: string | null }) {
	const parent = useNode(parentId ?? undefined);
	const crumbs = useBreadcrumbs(parentId ?? "");
	if (!parent) {
		return null;
	}
	const path = [...crumbs, parent].map((node) => labelOf(node.id, node.title));
	return (
		<div className="truncate text-xs font-light text-ink-faint" style={{ paddingLeft: GUTTER_LEFT }}>
			{path.join(" / ")}
		</div>
	);
}

function Branch({
	node,
	depth,
	parentOp,
	expanded,
	onToggle
}: {
	node: ArchiveTreeNode;
	depth: number;
	parentOp: string | null;
	expanded: ReadonlySet<string>;
	onToggle: (id: string) => void;
}) {
	const { row, children } = node;
	const hasChildren = children.length > 0;
	const open = hasChildren && expanded.has(row.id);
	const done = row.status === "done";
	const cancelled = row.status === "cancelled";
	/* Дата — только там, где началась своя волна архивации: у корня и у ветки, выкинутой
	   отдельно раньше родителя. Каскадные потомки её не повторяют — она та же. */
	const ownWave = row.archivedOp !== parentOp;

	return (
		<>
			<div className="row group" style={{ paddingLeft: GUTTER_LEFT + depth * INDENT }}>
				{Array.from({ length: depth }, (_, level) => (
					<span
						key={level}
						className="indent-guide"
						style={{ left: GUTTER_LEFT + level * INDENT + GUIDE_OFFSET }}
					/>
				))}
				<span className="flex shrink-0 items-center self-start pt-[3px]">
					<button
						type="button"
						aria-label={open ? "свернуть" : "развернуть"}
						aria-expanded={hasChildren ? open : undefined}
						disabled={!hasChildren}
						className="btn-icon size-[15px] rounded-xs"
						onClick={() => onToggle(row.id)}
					>
						{hasChildren && <Chevron open={open} />}
					</button>
					<span
						className="bullet relative"
						data-kind={row.role === "project" ? "project" : row.role === "focus" ? "focus" : undefined}
						data-tone={done || cancelled ? "muted" : undefined}
					>
						{hasChildren && !open && <span className="bullet-halo" />}
						<span className="bullet-dot relative" />
					</span>
				</span>
				<span
					className={[
						"row-title min-w-0 truncate",
						done ? "text-ink-muted line-through decoration-ink-faint" : "",
						cancelled ? "text-ink-faint line-through decoration-danger/60" : "",
						done || cancelled ? "" : "text-ink"
					].join(" ")}
				>
					{row.title || <span className="text-ink-faint">без названия</span>}
				</span>
				<span className="tabular text-xs font-light text-ink-faint">
					{ownWave ? row.archivedAt?.toLocaleDateString("ru") : ""}
				</span>
			</div>
			{open &&
				children.map((child) => (
					<Branch
						key={child.row.id}
						node={child}
						depth={depth + 1}
						parentOp={row.archivedOp}
						expanded={expanded}
						onToggle={onToggle}
					/>
				))}
		</>
	);
}
